import React, { useState } from 'react';
import * as S from './styles';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';

interface AutomaticFirmwareUpdatesModalProps {
  show: boolean;
  onClose: () => void;
  onProceed: () => void;
}

const AutomaticFirmwareUpdatesModal: React.FC<
  AutomaticFirmwareUpdatesModalProps
> = ({ show, onClose, onProceed }) => {
  const getText = useTranslationHelper();
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleProceed = () => {
    if (isChecked) {
      onProceed();
    }
  };

  return (
    <S.StyledModal
      id="automatic-firmware-updates-modal"
      show={show}
      onClose={onClose}
      header={
        <S.ModalTitle>
          {getText(
            'modal.automaticFirmwareUpdatesTitle',
            'Automatic Firmware Updates'
          )}
        </S.ModalTitle>
      }
      maxWidth="480px"
      closeButton={true}
      footer={
        <S.StyledGroup>
          <S.CancelButton
            appearance="secondary"
            onClick={onClose}
          >
            {getText('modal.cancel', 'Cancel')}
          </S.CancelButton>
          <S.ProceedButton
            onClick={handleProceed}
            disabled={!isChecked}
          >
            {getText('modal.proceed', 'Proceed')}
          </S.ProceedButton>
        </S.StyledGroup>
      }
      alignFooter="end"
    >
      <S.ModalContent>
        <S.FirstParagraph>
          {getText(
            'modal.automaticFirmwareDescription1',
            'To enroll in the HP All-In Plan, you must allow automatic firmware updates. These allow HP to provide improved data, network security and new features for your printer.'
          )}
        </S.FirstParagraph>

        <S.SecondParagraph>
          {getText(
            'modal.automaticFirmwareDescription2',
            'Note that this printer is intended to work only with cartridges that have a new or reused HP chip or circuitry, and it uses dynamic security measures to block cartridges using a non-HP chip or circuitry. Periodic firmware updates will maintain the effectiveness of these measures and block cartridges that previously worked. A reused HP chip or circuitry enables the use of reused, remanufactured, and refilled cartridges. For more information, go to'
          )}{' '}
          <S.Link
            href="https://www.hp.com/learn/ds"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getText('modal.learnMoreLink', 'www.hp.com/learn/ds')}.
          </S.Link>
        </S.SecondParagraph>

        <S.ThirdParagraph>
          {getText(
            'modal.automaticFirmwareDescription3',
            'If you cancel your HP All-In Plan, you can change your update settings.'
          )}
        </S.ThirdParagraph>

        <S.CheckboxContainer>
          <S.Checkbox
            type="checkbox"
            id="firmware-agreement"
            checked={isChecked}
            onChange={handleCheckboxChange}
          />
          <S.CheckboxLabel htmlFor="firmware-agreement">
            {getText(
              'modal.firmwareAgreement',
              'I understand that the printer will automatically check for and install available updates.'
            )}
          </S.CheckboxLabel>
        </S.CheckboxContainer>
      </S.ModalContent>
    </S.StyledModal>
  );
};

export default AutomaticFirmwareUpdatesModal;
